/**
 * Single source of truth for the site's canonical host, its language set and
 * every rule that turns one of them into a URL.
 *
 * Why this module exists
 * ----------------------
 * 1. The canonical host was hard-coded in more than one place. `MetaHelmet.js`
 *    read `REACT_APP_SITE_URL` with a fallback, while the SEO landing pages
 *    carried absolute canonicals built on a different literal host — so the two
 *    could disagree about which domain the page belonged to, which is the one
 *    piece of metadata that must never be ambiguous.
 * 2. `MetaHelmet.js` derived the hreflang set from the canonical it was handed,
 *    and the landing pages handed it the ENGLISH canonical in every language.
 *    So `/fr/...` and `/ar/...` declared the English URL as canonical and had
 *    their hreflang set derived from it.
 * 3. The hreflang derivation then existed twice — here and in MetaHelmet.
 *
 * Everything below is dependency-free plain ESM (no React, no JSX) so the public
 * test harness can import it directly with `await import()`.
 */

export const LANGS = ["en", "fr", "ar"];

/** Locale served to a visitor whose language is unknown. Also x-default. */
export const XDEFAULT = "en";

export const DEFAULT_CANONICAL_BASE = "https://routefacilecarrental.com";

/**
 * The canonical host. `process.env.REACT_APP_SITE_URL` is inlined by the build,
 * so production can be repointed without a code change; the literal fallback is
 * for tests and for a build where the variable is unset.
 */
export function canonicalBase() {
  const fromEnv =
    typeof process !== "undefined" && process.env
      ? process.env.REACT_APP_SITE_URL
      : undefined;
  return String(fromEnv || DEFAULT_CANONICAL_BASE).replace(/\/+$/, "");
}

/** Narrow an arbitrary language value to a supported one. */
export function normalizeLang(lang) {
  return LANGS.includes(lang) ? lang : XDEFAULT;
}

/** Collapse a path: leading slash, no duplicate slashes, no trailing slash. */
export function normalizePath(path) {
  let p = path || "/";
  if (!p.startsWith("/")) p = "/" + p;
  p = p.replace(/\/{2,}/g, "/");
  if (p.length > 1 && p.endsWith("/")) p = p.replace(/\/+$/, "");
  return p;
}

function baseHost() {
  try {
    return new URL(canonicalBase()).hostname.replace(/\.$/, "");
  } catch {
    return "";
  }
}

/**
 * True when a URL is absolute, parses, and belongs to the canonical host.
 * A URL on the wrong host is the failure mode that must never pass silently.
 */
export function isCanonicalHost(url) {
  if (!url || typeof url !== "string") return false;
  try {
    const u = new URL(url);
    return u.protocol === "https:" && u.hostname.replace(/\.$/, "") === baseHost();
  } catch {
    return false;
  }
}

/** The locale prefix of a URL, or XDEFAULT when it carries none. */
export function langOfUrl(url) {
  try {
    const parts = new URL(url).pathname.split("/").filter(Boolean);
    return LANGS.includes(parts[0]) ? parts[0] : XDEFAULT;
  } catch {
    return XDEFAULT;
  }
}

/**
 * Rewrite a URL so it points at the locale being served, on the canonical host.
 *
 * Returns "" when there is nothing trustworthy to rewrite (empty, unparseable,
 * or a host that is not the canonical host) — an absent canonical is correct,
 * a canonical pointing at the wrong host is not.
 */
export function localizeCanonical(url, lang) {
  const target = normalizeLang(lang);
  if (!url || typeof url !== "string") return "";
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return "";
  }
  if (!isCanonicalHost(url)) return "";
  const parts = parsed.pathname.split("/").filter(Boolean);
  if (parts.length === 0) return `${canonicalBase()}/${target}`;
  if (LANGS.includes(parts[0])) parts[0] = target;
  else parts.unshift(target);
  return `${canonicalBase()}/${parts.join("/")}`;
}

/**
 * The hreflang set for a URL: en, fr, ar and x-default, all on the same path.
 *
 * PREFIX-SWAP ONLY — correct for pages where the slug is identical across all
 * languages (city pages, airports, static pages). For /blogs/ pages where the
 * slug differs per language, this returns [] to force callers to provide explicit
 * hreflangs built from getCrossLanguageSlugs(). The old behaviour of blindly
 * swapping the prefix for blog pages produced 176 broken hreflang URLs that
 * 301-redirected to the correct canonical — every internal redirect Sitebulb
 * reported.
 *
 * `knownPaths`, when supplied, is the set of paths that actually exist
 * (see seo-routes.js); an alternate pointing at a path that does not exist is
 * dropped rather than advertised to crawlers.
 */
export function hreflangsFor(url, { knownPaths } = {}) {
  if (!url || typeof url !== "string") return [];
  if (!isCanonicalHost(url)) return [];
  const parts = new URL(url).pathname.split("/").filter(Boolean);
  if (!LANGS.includes(parts[0])) return [];
  // Article detail pages (/blogs/:slug) have locale-specific slugs, so
  // prefix-swapping them would create broken hreflang URLs. The blog INDEX
  // (/en|fr|ar/blogs) is different: all three locales share the same path.
  // Keep its reciprocal alternates in the client-rendered DOM; the prerendered
  // HTML already emits them, and hydration must not remove them.
  if (parts[1] === "blogs" && parts.length > 2) return [];
  const suffix = "/" + parts.slice(1).join("/");
  const entries = [
    ...LANGS.map((l) => ({ hreflang: l, href: `${canonicalBase()}/${l}${suffix}` })),
    { hreflang: "x-default", href: `${canonicalBase()}/${XDEFAULT}${suffix}` },
  ];
  if (!knownPaths) return entries;
  return entries.filter((e) => knownPaths.has(new URL(e.href).pathname));
}

/** Force any URL or path onto the canonical host with a clean path. */
export function normalizeCanonical(input) {
  if (!input) return "";
  try {
    if (/^https?:\/\//i.test(input)) {
      return `${canonicalBase()}${normalizePath(new URL(input).pathname)}`;
    }
    return `${canonicalBase()}${normalizePath(input)}`;
  } catch {
    return `${canonicalBase()}${normalizePath(String(input))}`;
  }
}

/**
 * The best URL for a page in another language when the page itself did NOT
 * declare an alternate for it, or "" when no such page is known to exist.
 *
 * Why "": the language switcher used to prefix-swap unconditionally, so on a
 * blog article that exists in French and Arabic only, the English entry pointed
 * at `/en/blogs/<french-slug>` — a URL that 404s (Screaming Frog found two of
 * exactly that shape on 2026-11-02, both crawled from the article's own
 * switcher). An article slug is per-language, so the honest answer for a
 * missing translation is "this page has no English version", and the caller
 * must not render the link at all. Prefix-swapping is still right for every
 * other route (static pages, city/airport pages, the /blogs index), whose path
 * is identical in all three languages.
 */
export function langPathTarget(pathname, code) {
  const target = normalizeLang(code);
  const parts = normalizePath(pathname).split("/").filter(Boolean);
  // /<lang>/blogs/<slug> — article detail. Never invent a slug.
  const blogsAt = parts.indexOf("blogs");
  if (blogsAt !== -1 && parts.length > blogsAt + 1) return "";
  if (LANGS.includes(parts[0])) parts[0] = target;
  else parts.unshift(target);
  return "/" + parts.join("/");
}

/**
 * Rewrite an absolute site URL (or a site-relative path) to the locale being
 * served, for breadcrumbs and in-page links. Commercial pages used to send
 * French and Arabic readers to the English path, leaving their language
 * mid-journey.
 */
export function localizePath(url, lang) {
  const target = normalizeLang(lang);
  if (!url || typeof url !== "string") return url;
  if (/^https?:\/\//i.test(url)) {
    const localized = localizeCanonical(url, target);
    return localized || url;
  }
  const parts = url.split("/").filter(Boolean);
  if (parts.length === 0) return `/${target}`;
  if (LANGS.includes(parts[0])) parts[0] = target;
  else parts.unshift(target);
  return `/${parts.join("/")}`;
}
